import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../context/AnalyticsContext';
import DateRangeFilter from './filters/DateRangeFilter';
import AgeFilter from './filters/AgeFilter';
import { Search } from 'lucide-react';

interface HeroFiltersProps {
  onMailingListScroll: (e: React.MouseEvent) => void;
}

const HeroFilters: React.FC<HeroFiltersProps> = ({ onMailingListScroll }) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  
  // State for hero filters
  const [heroFilters, setHeroFilters] = useState({
    startDate: undefined as string | undefined,
    endDate: undefined as string | undefined,
    ages: [] as number[]
  });

  const handleDateRangeChange = useCallback((startDate: Date | null, endDate: Date | null) => {
    setHeroFilters(prev => ({
      ...prev,
      startDate: startDate ? startDate.toISOString().split('T')[0] : undefined,
      endDate: endDate ? endDate.toISOString().split('T')[0] : undefined
    }));
  }, []);

  const handleAgesChange = useCallback((ages: number[]) => {
    setHeroFilters(prev => ({
      ...prev,
      ages: ages
    }));
  }, []);

  const handleHeroSearch = useCallback(() => {
    trackEvent('landing_page_hero_search_clicked', { 
      hasDateRange: !!(heroFilters.startDate && heroFilters.endDate),
      hasAges: heroFilters.ages.length > 0
    });
    
    const searchParams = new URLSearchParams();
    
    if (heroFilters.startDate) {
      searchParams.set('startDate', heroFilters.startDate);
    }
    if (heroFilters.endDate) {
      searchParams.set('endDate', heroFilters.endDate);
    }
    if (heroFilters.ages.length > 0) {
      searchParams.set('ages', heroFilters.ages.join(','));
    }

    navigate(`/search?${searchParams.toString()}`);
  }, [heroFilters.startDate, heroFilters.endDate, heroFilters.ages, trackEvent, navigate]);

  return (
    <div className="hero-filters">
      <div className="hero-filter-row">
        <DateRangeFilter 
          onDateRangeChange={handleDateRangeChange}
          initialStartDate={heroFilters.startDate}
          initialEndDate={heroFilters.endDate}
        />
        <AgeFilter 
          onAgesChange={handleAgesChange}
          initialAges={heroFilters.ages}
        />
        <button
          className="btn btn--primary hero-search-btn"
          onClick={handleHeroSearch}
        >
          <Search className="icon icon--sm" />
          Search Camps
        </button>
      </div>
      <div className="hero-disclaimer">
        Currently available in Colorado. Not your location? <button className="hero-disclaimer-link" onClick={onMailingListScroll}>Sign up for updates</button>.
      </div>
    </div>
  );
};

export default HeroFilters;
